//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { ChainId } from './Enums'
import { ProviderReadOnly } from './SchemaTypes'
import { logOT } from './loggingShared'

export async function findBlockByTimestamp(providerReadOnly: ProviderReadOnly, targetTimestamp: number) {
  logOT.debug('findBlockByTimestamp', targetTimestamp)
  let upperBlock = await providerReadOnly.getBlock('latest')
  if (!upperBlock) {
    throw new Error('Failed to retrieve the latest block')
  }
  const network = await providerReadOnly.getNetwork()
  let lowerBlock
  if (Number(network.chainId) == ChainId.HARDHAT) {
    lowerBlock = await providerReadOnly.getBlock(1)
  } else if (Number(network.chainId) === ChainId.FUJI || Number(network.chainId) === ChainId.AVALANCHE) {
    const blocksBack = Math.floor((Date.now() / 1000 - targetTimestamp) / 1) // once every 1.5 second rounded to 1 seconf
    lowerBlock = await providerReadOnly.getBlock(upperBlock.number - blocksBack) // Searching backwards 40,000 blocks as a start
  } else {
    const blocksBack = Math.floor((Date.now() / 1000 - targetTimestamp) / 10) // once every 14 second rounded to 10 seconds
    lowerBlock = await providerReadOnly.getBlock(upperBlock.number - blocksBack) // Searching backwards 40,000 blocks as a start
  }
  if (!lowerBlock) {
    throw new Error('Failed to retrieve the latest block')
  }

  logOT.debug('findBlockByTimestamp before while', lowerBlock.number, upperBlock.number)
  logOT.debug(
    'findBlockByTimestamp before while',
    lowerBlock.timestamp / 1e6,
    targetTimestamp / 1e6,
    lowerBlock.timestamp > targetTimestamp
  )
  while (lowerBlock.timestamp > targetTimestamp && lowerBlock.number > 1) {
    upperBlock = lowerBlock
    lowerBlock = await providerReadOnly.getBlock(Math.max(1, lowerBlock.number - 40000))
    if (!lowerBlock) {
      throw new Error('Failed to retrieve the latest block')
    }
    logOT.debug(
      'findBlockByTimestamp Loop while',
      lowerBlock.timestamp,
      targetTimestamp,
      lowerBlock.timestamp > targetTimestamp
    )
  }
  if (!lowerBlock) {
    throw new Error('Failed to retrieve the latest block')
  }
  if (!upperBlock) {
    throw new Error('Failed to retrieve the latest block')
  }
  logOT.debug('findBlockByTimestamp range', lowerBlock.number, upperBlock.number)
  // Binary search between lowerBlock and upperBlock
  while (lowerBlock.number < upperBlock.number) {
    const midBlockNumber = Math.floor((upperBlock.number + lowerBlock.number) / 2)

    const midBlock = await providerReadOnly.getBlock(midBlockNumber)
    if (!midBlock) {
      throw new Error('Failed to retrieve the latest block')
    }
    if (midBlock.timestamp > targetTimestamp) {
      upperBlock = midBlock
    } else {
      lowerBlock = midBlock
    }

    if (upperBlock.number - lowerBlock.number <= 1) {
      logOT.debug('findBlockByTimestamp finsihed', lowerBlock.number)
      return lowerBlock.number // or upperBlock.number based on your use case
    }
  }
  logOT.debug('findBlockByTimestamp Fallback', lowerBlock.number)
  return lowerBlock.number // Fallback
}

/**
 * Formats a timestamp (in seconds or ms) as a human-friendly age string.
 * Example outputs: "just now", "2 mins 5 secs ago", "3 hrs 15 mins ago", "1 day 4 hrs ago"
 */
export function formatAgeFromTimestamp(timestamp: number, delta: number): string {
  // Normalize to seconds if in ms

  if (timestamp > 1e12) timestamp = Math.floor(timestamp / 1000)
  let diff = Math.floor(Date.now() / 1000) + delta - timestamp
  if (diff < 0) diff = 0

  const minutes = Math.floor(diff / 60)
  if (minutes < 60) {
    return `${minutes} min${minutes === 1 ? '' : 's'} ago`
  }

  const hours = Math.floor(minutes / 60)
  const remMinutes = minutes % 60
  if (hours < 24) {
    return remMinutes > 0
      ? `${hours} hr${hours === 1 ? '' : 's'} ${remMinutes} min${remMinutes === 1 ? '' : 's'} ago`
      : `${hours} hr${hours === 1 ? '' : 's'} ago`
  }

  const days = Math.floor(hours / 24)
  const remHours = hours % 24
  return remHours > 0
    ? `${days} day${days === 1 ? '' : 's'} ${remHours} hr${remHours === 1 ? '' : 's'} ago`
    : `${days} day${days === 1 ? '' : 's'} ago`
}
